/**
 * @file 项目接口服务
 */

import type {
    ProjectListParams,
    ProjectListData,
    ProjectRenameParams,
    ProjectDeleteParams,
    MemberSearchParams,
    MemberSearchData,
    MemberSubmitParams,
} from './interface';
import type {
    ProjectInfoData,
    EpisodeListData,
    EpisodeVersionListData,
    DeletedEpisodeListData,
    EpisodeBatchResult,
    AssetInfoData,
    ProjectSettings,
    MemberRoleItem,
    AssetExtractStatus,
} from '@/types/project';
import type {ProjectId} from '@/types';
import {request} from '@/services';

const acsToken = true;

/**
 * 获取项目列表
 * @see 4.1 AI剧获取接口 GET /proxy-aicomic/homepage/asset/comic/list
 */
export const getProjectList = (params: ProjectListParams) =>
    request.get<ProjectListData>('proxy-aicomic/homepage/asset/comic/list', {params});

/**
 * AI 剧重命名
 */
export const renameProject = (data: ProjectRenameParams) =>
    request.post<null>('proxy-aicomic/homepage/asset/project/rename', data, {silent: false});

/**
 * AI 剧删除
 */
export const deleteProject = (data: ProjectDeleteParams) =>
    request.post<null>('proxy-aicomic/homepage/asset/project/delete', data, {silent: false});

/**
 * 获取项目信息（/proxy-aicomic/project/info GET）
 * 包含：project_info、progress_info、point_info、members
 */
export const fetchProjectInfo = (projectId: ProjectId) =>
    request.get<ProjectInfoData>('proxy-aicomic/project/info', {
        params: {projectId},
    });

/**
 * 获取剧集列表（/proxy-aicomic/episodes/list GET），支持分页与按名称模糊搜索
 */
export const fetchEpisodeList = (
    projectId: ProjectId,
    currentPage = 1,
    pageSize = 10,
    keyword = '',
) =>
    request.get<EpisodeListData>('proxy-aicomic/episodes/list', {
        params: {
            projectId,
            currentPage,
            pageSize,
            // 空串转 undefined，让请求直接不带这个 query
            keyword: keyword.trim() || undefined,
        },
    });

/**
 * 剧集重命名（/proxy-aicomic/episode/rename POST）
 */
export const renameEpisode = (data: {projectId: ProjectId; episodeId: number; title: string}) =>
    request.post<null>('proxy-aicomic/episode/rename', data, {timeoutToast: false});

/**
 * 批量移入回收站（/proxy-aicomic/episode/delete POST）
 * 单集删除传长度为 1 的数组，不另留 episodeId 字段
 */
export const deleteEpisodes = (data: {projectId: ProjectId; episodeIds: number[]}) =>
    request.post<EpisodeBatchResult>('proxy-aicomic/episode/delete', data, {timeoutToast: false});

/**
 * 回收站列表（/proxy-aicomic/episode/deleted/list GET）
 */
export const fetchDeletedEpisodeList = (
    projectId: ProjectId,
    currentPage = 1,
    pageSize = 20,
    keyword = '',
) =>
    request.get<DeletedEpisodeListData>('proxy-aicomic/episode/deleted/list', {
        params: {
            projectId,
            currentPage,
            pageSize,
            keyword: keyword.trim() || undefined,
        },
    });

/**
 * 批量恢复（/proxy-aicomic/episode/deleted/restore POST）
 */
export const restoreEpisodes = (data: {projectId: ProjectId; episodeIds: number[]}) =>
    request.post<EpisodeBatchResult>('proxy-aicomic/episode/deleted/restore', data, {
        timeoutToast: false,
    });

/**
 * 永久删除 / 清空回收站（/proxy-aicomic/episode/deleted/purge POST）
 */
export const purgeEpisodes = (data: {projectId: ProjectId; episodeIds?: number[]; all?: boolean}) =>
    request.post<EpisodeBatchResult>('proxy-aicomic/episode/deleted/purge', data, {
        timeoutToast: false,
    });

/**
 * 获取资产信息（/proxy-aicomic/asset/info GET）
 */
export const fetchAssetInfo = (projectId: ProjectId) =>
    request.get<AssetInfoData>('proxy-aicomic/asset/info/v2', {params: {projectId}});

/**
 * 资产提取状态轮询（/proxy-aicomic/asset/extract/status/polling GET）
 */
export const fetchAssetExtractStatus = (projectId: ProjectId, taskId: string) =>
    request.get<{status: AssetExtractStatus; taskResultMsg?: string}>(
        'proxy-aicomic/asset/extract/task/polling',
        {
            params: {projectId, taskId},
        },
    );

/**
 * 更新项目信息（/proxy-aicomic/project/update POST）
 */
export const updateProjectInfo = (info: ProjectSettings) =>
    request.post<void>('proxy-aicomic/project/update', {...info});

/**
 * 获取剧集视频版本列表（/video/episode/list GET）
 * @param projectId 项目 ID
 * @param episodeId 分集标识 ID
 */
export const fetchEpisodeVersionList = (params: {projectId: ProjectId; episodeId: number}) =>
    request.get<EpisodeVersionListData>('proxy-aicomic/video/episode/list', {params});

/**
 * 获取成员角色列表（/proxy-aicomic/member/roles GET）
 */
export const fetchMemberRoleList = () =>
    request.get<MemberRoleItem[]>('proxy-aicomic/member/roles');

/**
 * 编辑视频批注（/proxy-aicomic/video/episode/annotation/edit POST）
 */
export const editVideoAnnotation = (params: {
    projectId: ProjectId;
    episodeId: number;
    videoId: string;
    annotation?: string;
    selectionStatus?: number;
}) => request.post<void>('proxy-aicomic/video/episode/annotation/edit', params);

/* 重新上传视频渲染（/proxy-aicomic/video/editor/retryRender POST）
 * @param projectId 项目 ID
 * @param episodeId 分集 ID
 * @param videoId 视频版本 ID
 */
export const retryVideoRender = (params: {
    projectId: ProjectId;
    episodeId: number;
    videoId: string;
}) => request.post<void>('proxy-aicomic/video/editor/retryRender', params);

/**
 * 搜索项目成员（/proxy-aicomic/member/search GET）
 */
export const searchMember = (params: MemberSearchParams) =>
    request.get<MemberSearchData>('proxy-aicomic/member/search', {params, acsToken});

/**
 * 提交成员列表（/proxy-aicomic/member/submit POST）
 */
export const submitMembers = (params: MemberSubmitParams) =>
    request.post<void>('proxy-aicomic/member/submit', params, {acsToken});
